import type { RuntimeComponentDownloadStatus } from '../shared/desktop-api';

/**
 * THE DOWNLOAD BAR ONLY MOVES FORWARD. Main measures the packs it queued in this
 * process: a restart re-counts only the packs still missing, a pack added later
 * grows the total, and a retry drops the failed pack's partial bytes, so its
 * fraction can fall while the one download the operator is watching continues
 * (owner, 2026-09-29: progress never visibly goes backward). The highest fraction
 * shown is kept until the download completes, across remounts and restarts.
 * Display only: main's status is never changed.
 */
export const downloadFloorKey = 'cpv6-runtime-download-floor';
export function floorRuntimeDownload(
  status: RuntimeComponentDownloadStatus,
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null,
  now = Date.now(),
): RuntimeComponentDownloadStatus {
  try {
    // Only a finished download ends the run; "idle" can be the moment before main starts again.
    if (status.state === 'complete') {
      storage?.removeItem(downloadFloorKey);
      return status;
    }
    if (status.state !== 'downloading' && status.state !== 'failed') return status;
    // A floor left by a download that never reported completing stops counting after a few hours.
    const saved = JSON.parse(storage?.getItem(downloadFloorKey) ?? 'null') as {
      fraction?: unknown;
      at?: unknown;
    } | null;
    const floor =
      saved &&
      typeof saved.fraction === 'number' &&
      Number.isFinite(saved.fraction) &&
      typeof saved.at === 'number' &&
      now - saved.at < 6 * 3_600_000
        ? Math.min(1, saved.fraction)
        : 0;
    const fraction = Math.max(floor, status.fraction);
    storage?.setItem(downloadFloorKey, JSON.stringify({ fraction, at: now }));
    return fraction === status.fraction ? status : { ...status, fraction };
  } catch {
    return status;
  }
}
